import type { ToneName } from './tokens';

/*
 * «Форми 2» — the look of the whole system (chosen 28.10, tag `design/v4`), shared by the site and the
 * app: the warm paper, the near-black violet ink and dark (his «тёмно-тёмно-фиолетовый», never black, never
 * green), the softer inks as the same ink with transparency (so they sit on a coloured card too — never
 * grey), the soft gradients of the cards and the deep ones of the pictures' shapes.
 */

export const forms2 = {
  paper: '#F5F3EF',
  /** the hairline of a block, a row's divider */
  line: '#E4E0D7',
  ink: '#1F1A35',
  inkSoft: 'rgba(31,26,53,0.64)',
  inkFaint: 'rgba(31,26,53,0.42)',
  /** the main button, the chosen day */
  dark: '#241A45',
  /** what is on (an open menu item, a chosen tool): the paper a shade darker */
  on: '#E9E5DC',
  /** a counter: the orange gradient */
  count: ['#FF9A72', '#FF6B3D'] as const,
  /** a big block's corner */
  radius: 28,
} as const;

/** Each colour as a soft gradient from light to its tone; the words in the colour's own ink. */
export const GRAD = {
  lime: { from: '#F4FBD9', to: '#DCF28A', ink: '#4D6800' },
  sky: { from: '#EBF4FD', to: '#C3DEF8', ink: '#1F5E9C' },
  cobalt: { from: '#EAEDFF', to: '#BDC6FB', ink: '#3445B5' },
  lavender: { from: '#F4ECFF', to: '#DAC8FA', ink: '#6544C4' },
  pink: { from: '#FFEEF5', to: '#F8C5DB', ink: '#A83D6E' },
  coral: { from: '#FFECE4', to: '#FFBEA4', ink: '#B4431C' },
  apricot: { from: '#FFF2E3', to: '#FFD5A9', ink: '#97520E' },
  butter: { from: '#FFF9DE', to: '#FFE596', ink: '#82620A' },
  mint: { from: '#E8F8F0', to: '#B8E8CE', ink: '#1C774B' },
  aqua: { from: '#E4F7F4', to: '#B2E6DE', ink: '#12736A' },
  sand: { from: '#F8F2E9', to: '#E8DCC8', ink: '#76582E' },
  stone: { from: '#F5F4F1', to: '#E4E2DC', ink: '#566861' },
  orange: { from: '#FFAE8C', to: '#FF6B3D', ink: '#FFFFFF' },
} as const;

export type Grad = keyof typeof GRAD;

/**
 * The deep gradient of a colour, for the shapes of the pictures: from a notch deeper than the card's own
 * colour into the saturated tone — a shape stands out of its card in the same hue, never blends in.
 */
export const DEEP: Record<Grad, readonly [string, string]> = {
  lime: ['#D3EE6E', '#A9D62E'],
  sky: ['#A9CDF5', '#6A9EE6'],
  cobalt: ['#A9B4F8', '#6676EC'],
  lavender: ['#C8B0F7', '#9575EA'],
  pink: ['#F4AECB', '#E27BA8'],
  coral: ['#FFAA8A', '#F7744B'],
  apricot: ['#FFC289', '#F4994A'],
  butter: ['#FFDD72', '#F2BB2A'],
  mint: ['#9EDFBE', '#4FBF89'],
  aqua: ['#96DDD2', '#3FB6A8'],
  sand: ['#DFCDB0', '#C2A176'],
  stone: ['#D6D2C9', '#ABA597'],
  orange: ['#FF9A72', '#FF6B3D'],
};

/** A subject's gradient by its tone. */
export const SUBJECT_GRAD: Record<ToneName, Grad> = {
  blue: 'sky',
  violet: 'lavender',
  pink: 'pink',
  red: 'coral',
  orange: 'apricot',
  yellow: 'butter',
  green: 'mint',
  teal: 'aqua',
  grey: 'stone',
};

/** The colour of a school event by its kind. */
export const EVENT_GRAD: Record<
  'holiday' | 'celebration' | 'meeting' | 'trip' | 'exam' | 'other',
  Grad
> = {
  holiday: 'mint',
  celebration: 'pink',
  meeting: 'butter',
  trip: 'sky',
  exam: 'lavender',
  other: 'aqua',
};

/** A grade's colour by its level (coral 1–3, butter 4–6, sky 7–9, lime 10–12). */
export function gradeGrad(points: number): Grad {
  if (points <= 3) return 'coral';
  if (points <= 6) return 'butter';
  if (points <= 9) return 'sky';
  return 'lime';
}
